import { NavLink, Link } from "react-router-dom";
import { LayoutDashboard, FileEdit, List, AlertTriangle, Users, LogOut, Building2, History, Settings, BookLock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Layout = ({ children, user, onLogout }) => {
  // Check if user is super-admin (admin without company)
  const isSuperAdmin = user?.is_admin && !user?.company_id;

  return (
    <div className="app-layout min-h-screen bg-[#09090B] flex flex-col">
      {/* Warning Tape */}
      <div className="warning-tape" />
      
      {/* Header */}
      <header className="app-header bg-[#09090B] border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 py-3">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500 flex items-center justify-center rounded-sm">
                <AlertTriangle className="w-6 h-6 text-black" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="font-black text-xl tracking-tight text-zinc-50" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  UNFALLMELDE TOOL
                </h1>
                <p data-testid="header-company-name" className="text-xs text-zinc-500 uppercase tracking-widest">
                  {user?.company_name || 'Arbeitsschutz'}
                </p>
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="flex flex-wrap items-center gap-1 max-w-full" aria-label="Hauptnavigation">
              <NavLink
                to="/"
                data-testid="nav-dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                    isActive 
                      ? 'bg-yellow-500/10 text-yellow-500' 
                      : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                  }`
                }
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </NavLink>
              
              <NavLink
                to="/neu"
                data-testid="nav-new-incident"
                aria-label="Neue Meldung"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                    isActive 
                      ? 'bg-yellow-500/10 text-yellow-500' 
                      : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                  }`
                }
              >
                <FileEdit className="w-4 h-4" />
                <span className="hidden sm:inline">Neue Meldung</span>
              </NavLink>
              
              <NavLink
                to="/unfaelle"
                data-testid="nav-incidents"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                    isActive 
                      ? 'bg-yellow-500/10 text-yellow-500' 
                      : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                  }`
                }
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Übersicht</span>
              </NavLink>

              {isSuperAdmin && (
                <>
                  <NavLink
                    to="/unternehmen"
                    data-testid="nav-companies"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                        isActive 
                          ? 'bg-yellow-500/10 text-yellow-500' 
                          : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                      }`
                    }
                  >
                    <Building2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Unternehmen</span>
                  </NavLink>
                  
                  <NavLink
                    to="/benutzer"
                    data-testid="nav-users"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                        isActive 
                          ? 'bg-yellow-500/10 text-yellow-500' 
                          : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                      }`
                    }
                  >
                    <Users className="w-4 h-4" />
                    <span className="hidden sm:inline">Benutzer</span>
                  </NavLink>
                  
                  <NavLink
                    to="/formulare"
                    data-testid="nav-form-builder"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                        isActive 
                          ? 'bg-yellow-500/10 text-yellow-500' 
                          : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                      }`
                    }
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Formulare</span>
                  </NavLink>
                  
                  <NavLink
                    to="/audit-log"
                    data-testid="nav-audit-log"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                        isActive 
                          ? 'bg-yellow-500/10 text-yellow-500' 
                          : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                      }`
                    }
                  >
                    <History className="w-4 h-4" />
                    <span className="hidden sm:inline">Audit-Log</span>
                  </NavLink>
                </>
              )}

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    data-testid="user-menu"
                    className="ml-2 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800"
                  >
                    <div className="w-8 h-8 bg-yellow-500/20 rounded-sm flex items-center justify-center mr-2">
                      <span className="text-yellow-500 font-bold text-sm">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="hidden md:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-zinc-50">{user?.name}</p>
                    <p className="text-xs text-zinc-500">{user?.email}</p>
                    {isSuperAdmin && (
                      <span className="badge-warning text-xs px-2 py-0.5 rounded-sm mt-1 inline-block">Super-Admin</span>
                    )}
                    {user?.company_name && (
                      <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {user.company_name}
                      </p>
                    )}
                  </div>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  {isSuperAdmin && (
                    <DropdownMenuItem asChild>
                      <Link
                        to="/betreiberhandbuch"
                        data-testid="operator-handbook-menu-link"
                        className="text-zinc-300 focus:text-yellow-500 focus:bg-yellow-500/10 cursor-pointer"
                      >
                        <BookLock className="w-4 h-4 mr-2" />
                        Betreiberhandbuch
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isSuperAdmin && <DropdownMenuSeparator className="bg-zinc-800" />}
                  <DropdownMenuItem 
                    onClick={onLogout}
                    data-testid="logout-button"
                    className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="app-main max-w-7xl mx-auto w-full min-w-0 flex-1 px-4 sm:px-6 lg:px-8 py-8 pb-12">
        {children}
      </main>

      {/* Footer */}
      <footer className="app-footer mt-auto bg-zinc-950 border-t border-zinc-800 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-center gap-4 text-xs text-zinc-500">
          <Link to="/impressum" className="hover:text-zinc-300 transition-colors">Impressum</Link>
          <span>|</span>
          <Link to="/datenschutz" className="hover:text-zinc-300 transition-colors">Datenschutz</Link>
          <span>|</span>
          <Link to="/technische-dokumentation" className="hover:text-yellow-500 transition-colors">Technische Dokumentation</Link>
          <span>|</span>
          <Link to="/av-vertrag" className="hover:text-yellow-500 transition-colors">AV-Vertrag</Link>
        </div>
      </footer>
    </div>
  );
};
